import { VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoWithoutSection } from '@components/cockpit/panels/monitoring/monitoring.types';
import { isSensorOrSubSensorInfoOfType } from '@components/cockpit/panels/monitoring/monitoring.utils';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';

import { ActionEnum } from '@/types/commons/commons.types';
import { MapIdEnum } from '@/types/map.types';
import { SensorTypeEnum, SubSensorTypeEnum } from '@/types/sensor/sensors';

import EmergencySensorPhoneNumber from '../EmergencySensorPhoneNumber';
import AutomatonCommands from './AutomatonCommands';
import CabinetCommands from './CabinetCommands';
import Dfb6Commands from './Dfb6Commands';
import FlirCommands from './FlirCommands';
import MajesCommands from './MajesCommands';
import SkyJackerCommands from './SkyJackerCommands';
import XentaCommands from './XentaCommands';

type SpecificCommandsProps = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoWithoutSection;
  inMonitoring?: boolean;
};

export function SpecificCommands({ sensorOrSubSensorInfo, inMonitoring = false }: Readonly<SpecificCommandsProps>) {
  switch (sensorOrSubSensorInfo.type) {
    case SensorTypeEnum.AUTOMATON: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.AUTOMATON)) {
        return <AutomatonCommands {...sensorOrSubSensorInfo} />;
      }
      return null;
    }
    case SensorTypeEnum.MAJES: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.MAJES)) {
        return <MajesCommands sensorOrSubSensorInfo={sensorOrSubSensorInfo} inMonitoring={inMonitoring} />;
      }
      return null;
    }
    case SensorTypeEnum.DFB6: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.DFB6)) {
        return <Dfb6Commands sensorOrSubSensorInfo={sensorOrSubSensorInfo} inMonitoring={inMonitoring} />;
      }
      return null;
    }
    case SubSensorTypeEnum.AUTOMATON_CABINET:
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SubSensorTypeEnum.AUTOMATON_CABINET)) {
        return <CabinetCommands {...sensorOrSubSensorInfo} />;
      }
      return null;
    case SensorTypeEnum.FLIR: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.FLIR)) {
        return <FlirCommands {...sensorOrSubSensorInfo} />;
      }
      return null;
    }
    case SensorTypeEnum.XENTA: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.XENTA)) {
        return <XentaCommands {...sensorOrSubSensorInfo} />;
      }
      return null;
    }
    case SensorTypeEnum.SKY_JACKER: {
      if (isSensorOrSubSensorInfoOfType(sensorOrSubSensorInfo, SensorTypeEnum.SKY_JACKER)) {
        return <SkyJackerCommands {...sensorOrSubSensorInfo} />;
      }
      return null;
    }
    default:
      return null;
  }
}

type CommandsProps = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoWithoutSection;
  mapId: MapIdEnum;
};

export default function Commands({ sensorOrSubSensorInfo, mapId }: Readonly<CommandsProps>) {
  const replayMode = mapId === MapIdEnum.REPLAY;
  const specificCommands = SpecificCommands({ sensorOrSubSensorInfo });
  const sensorAction = {
    action: ActionEnum.COMMAND,
    sensorFamily: sensorFamilyFromSensorType[sensorOrSubSensorInfo.type],
  };
  const canDoCommand = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorAction));

  if (replayMode || specificCommands === null || !canDoCommand) {
    return null;
  }
  return (
    <VStack gap={0.25} width="100%" height="100%" textStyle="screenStd">
      {specificCommands}
      <EmergencySensorPhoneNumber configuration={sensorOrSubSensorInfo.configuration} />
    </VStack>
  );
}
